var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Tab administration modal (CZNA-360 — variant C redesign).
 *
 * Hosts the drag-drop reorderable list of tabs plus rename/delete/add
 * actions that used to live inline in the horizontal tab strip
 * (`TabListEditor`, TASK-181 WP-B CP-1). The strip itself is now a thin
 * chip row (see `TabListEditor.tsx`) that only supports 1-click select
 * and opens this modal via its "⋯" button for anything else.
 *
 * All mutations dispatch straight into the existing `useEditorState`
 * reducer (`moveTab` / `updateTabLabel` / `addTab` / `removeTab`) — same
 * live-dispatch behaviour as before. There is no modal-local draft/commit
 * layer: closing the modal ("Zavřít") does not persist or discard
 * anything by itself, it just hides the UI. The single commit point
 * remains the global Save button in `FormConfigVisualEditor`'s
 * `PaneFooter`, unchanged.
 *
 * @hello-pangea/dnd a11y: same Czech `announce` callbacks as before
 * (Risk R3) so screen readers hear "Posunul jste záložku X na pozici N
 * z M" while dragging inside the modal.
 */
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DragDropContext, Droppable, Draggable, } from '@hello-pangea/dnd';
import { Button, IconButton, Modal, TextField } from '@folio/stripes/components';
import { readCsLabel, writeCsLabel } from './labelUtils';
var TabManagementModal = function (_a) {
    var open = _a.open, tabs = _a.tabs, activeTabIdx = _a.activeTabIdx, onSelectTab = _a.onSelectTab, dispatch = _a.dispatch, onClose = _a.onClose, recordTypeLabel = _a.recordTypeLabel;
    var intl = useIntl();
    var _b = useState(null), editingIdx = _b[0], setEditingIdx = _b[1];
    var _c = useState(''), draftLabel = _c[0], setDraftLabel = _c[1];
    var liveRegionRef = useRef(null);
    var announce = useCallback(function (message) {
        if (liveRegionRef.current) {
            liveRegionRef.current.textContent = message;
        }
    }, []);
    var onDragStart = function (start) {
        var tab = tabs[start.source.index];
        announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.drag.start' }, { label: readCsLabel(tab === null || tab === void 0 ? void 0 : tab.label) || (tab === null || tab === void 0 ? void 0 : tab.id) || '?' }));
    };
    var onDragUpdate = function (update) {
        if (!update.destination)
            return;
        announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.drag.update' }, { position: update.destination.index + 1, total: tabs.length }));
    };
    var onDragEnd = function (result) {
        if (!result.destination) {
            announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.drag.cancel' }));
            return;
        }
        var from = result.source.index;
        var to = result.destination.index;
        if (from === to)
            return;
        dispatch({ type: 'moveTab', from: from, to: to });
        // Keep the active tab selected after reorder
        if (activeTabIdx === from)
            onSelectTab(to);
        else if (from < activeTabIdx && to >= activeTabIdx)
            onSelectTab(activeTabIdx - 1);
        else if (from > activeTabIdx && to <= activeTabIdx)
            onSelectTab(activeTabIdx + 1);
        announce(intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.drag.end' }, { position: to + 1, total: tabs.length }));
    };
    var startEdit = function (idx) {
        setEditingIdx(idx);
        setDraftLabel(readCsLabel(tabs[idx].label));
    };
    var commitEdit = function () {
        if (editingIdx === null)
            return;
        dispatch({
            type: 'updateTabLabel',
            tabIdx: editingIdx,
            label: writeCsLabel(tabs[editingIdx].label, draftLabel),
        });
        setEditingIdx(null);
    };
    var cancelEdit = function () {
        setEditingIdx(null);
        setDraftLabel('');
    };
    var handleAddTab = function () {
        var newId = "tab-".concat(Date.now());
        dispatch({
            type: 'addTab',
            tab: { id: newId, label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.newTabDefaultLabel' }), fields: [] },
        });
        onSelectTab(tabs.length);
    };
    var handleRemove = function (idx) {
        // Active-tab shift after removal (TASK-181 review IMPROVE I6):
        //   - removed === active   → fall back to the previous tab (or 0)
        //   - removed before active → activeTabIdx shifts down by one
        //   - removed after active  → no change
        dispatch({ type: 'removeTab', tabIdx: idx });
        if (idx === activeTabIdx) {
            onSelectTab(Math.max(0, idx - 1));
        }
        else if (idx < activeTabIdx) {
            onSelectTab(activeTabIdx - 1);
        }
    };
    var heading = recordTypeLabel
        ? intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.modal.heading' }, { recordType: recordTypeLabel })
        : intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.modal.headingGeneric' });
    var footer = (React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end' } },
        React.createElement(Button, { onClick: onClose, "data-testid": "tab-management-modal-close-btn" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.tabs.modal.close" }))));
    return (React.createElement(Modal, { open: open, label: heading, footer: footer, dismissible: true, onClose: onClose, size: "medium", "data-testid": "tab-management-modal" },
        React.createElement("div", { ref: liveRegionRef, "aria-live": "polite", "aria-atomic": "true", style: { position: 'absolute', left: '-9999px' }, "data-testid": "tab-management-live-region" }),
        React.createElement(DragDropContext, { onDragStart: onDragStart, onDragUpdate: onDragUpdate, onDragEnd: onDragEnd },
            React.createElement(Droppable, { droppableId: "tab-management-list" }, function (provided) { return (React.createElement("div", __assign({ ref: provided.innerRef }, provided.droppableProps, { "data-testid": "tab-management-list", style: { display: 'flex', flexDirection: 'column', gap: '0.25rem' } }),
                tabs.map(function (tab, idx) { return (React.createElement(Draggable, { key: tab.id, draggableId: tab.id, index: idx }, function (dragProvided, snapshot) { return (React.createElement("div", __assign({ ref: dragProvided.innerRef }, dragProvided.draggableProps, { "data-testid": "tab-management-row-".concat(idx), style: __assign(__assign({}, dragProvided.draggableProps.style), { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.5rem', borderRadius: '4px', backgroundColor: snapshot.isDragging ? '#f0f8ff' : 'transparent', border: '1px solid #e2e2e2' }) }),
                    React.createElement("span", __assign({}, dragProvided.dragHandleProps, { "data-testid": "tab-management-row-".concat(idx, "-handle"), role: "button", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.dragHandleAria' }), style: {
                            cursor: 'grab',
                            color: '#888',
                            fontSize: '0.95rem',
                            lineHeight: 1,
                            userSelect: 'none',
                            padding: '0 0.1rem',
                        } }), "\u22EE\u22EE"),
                    editingIdx === idx ? (React.createElement(TextField, { value: draftLabel, onChange: function (e) { return setDraftLabel(e.target.value); }, onBlur: commitEdit, onKeyDown: function (e) {
                            if (e.key === 'Enter')
                                commitEdit();
                            else if (e.key === 'Escape')
                                cancelEdit();
                        }, autoFocus: true, marginBottom0: true, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.editLabelAria' }), "data-testid": "tab-management-row-".concat(idx, "-label-input") })) : (React.createElement("span", { "data-testid": "tab-management-row-".concat(idx, "-label"), style: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }, title: readCsLabel(tab.label) || tab.id }, readCsLabel(tab.label) || tab.id)),
                    editingIdx !== idx && (React.createElement(IconButton, { icon: "edit", onClick: function () { return startEdit(idx); }, ariaLabel: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.editLabelAria' }), "data-testid": "tab-management-row-".concat(idx, "-edit-btn") })),
                    React.createElement(IconButton, { icon: "trash", onClick: function () { return handleRemove(idx); }, ariaLabel: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.tabs.removeAria' }), "data-testid": "tab-management-row-".concat(idx, "-remove-btn") }))); })); }),
                provided.placeholder)); })),
        React.createElement(Button, { onClick: handleAddTab, "data-testid": "tab-management-add-btn", marginBottom0: true, buttonStyle: "default", style: { marginTop: '0.75rem' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.tabs.addTab" }))));
};
export default TabManagementModal;
